/**
 * Guarded transaction signer.
 *
 * Wraps the Kit `TransactionSigner` exposed by `@nullaone/wallet-react` with
 * detrust_ai's transaction-guard pipeline: decode → user confirmation dialog
 * → sign. Semantics ported verbatim from the pre-migration
 * `useBrowserWallet.signTransaction`:
 *
 * - Hot wallet: decode the transaction, block on
 *   `requestConfirmation(tx, decoded)` (the TransactionConfirmationDialog
 *   promise bridge — rejects with "Transaction rejected by user" on cancel,
 *   times out after 60s), then sign.
 * - Ledger: no dialog — the device provides its own confirmation UI. The
 *   package signer already handles last-used bookkeeping internally.
 */

import { useMemo } from "react";
import {
  assertIsTransactionWithinSizeLimit,
  isTransactionPartialSigner,
  type Transaction,
  type TransactionPartialSigner,
  type TransactionWithLifetime,
} from "@solana/kit";
import { useBrowserWallet } from "@nullaone/wallet-react";
import { decodeTransaction } from "../lib/transaction-decoder";
import { useTransactionGuardStore } from "../store/transaction-guard-store";

/**
 * Returns a `TransactionPartialSigner` (assignable wherever a Kit
 * `TransactionSigner` is expected) that runs the guard/confirm pipeline
 * before delegating to the wallet's signer. Null while disconnected.
 */
export function useGuardedSigner(): TransactionPartialSigner | null {
  const { signer, walletType } = useBrowserWallet();

  return useMemo(() => {
    if (!signer || !isTransactionPartialSigner(signer)) return null;
    const base = signer;

    const guarded: TransactionPartialSigner = {
      address: base.address,
      async signTransactions(transactions, config) {
        // Hot wallet only — Ledger has device-level confirmation.
        if (walletType === "hot") {
          for (const tx of transactions) {
            const decoded = decodeTransaction(tx);
            // Throws/rejects on user cancel or 60s timeout.
            await useTransactionGuardStore
              .getState()
              .requestConfirmation(tx, decoded);
          }
        }
        return base.signTransactions(transactions, config);
      },
    };
    return guarded;
  }, [signer, walletType]);
}

/**
 * Back-compat helper for call sites shaped `signTransaction(tx) → signed tx`.
 * Returns a copy of the transaction with the wallet's signature merged into
 * `signatures`; the input transaction is not mutated.
 */
export async function signWithSigner<
  T extends Transaction & TransactionWithLifetime,
>(signer: TransactionPartialSigner, tx: T): Promise<T> {
  // Runtime size check that also brands the type the signer interface needs.
  assertIsTransactionWithinSizeLimit(tx);
  const [sigDict] = await signer.signTransactions([tx]);
  if (!sigDict) {
    throw new Error("Signing failed: no signature dictionary returned");
  }
  return { ...tx, signatures: { ...tx.signatures, ...sigDict } };
}
